import { Client } from "@today/api/taker"
import { Button } from "baseui/button"
import { Input } from "baseui/input"
import { FormControl } from "baseui/form-control"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useFetcher } from "@today/api"
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaTimesCircle,
} from "react-icons/fa"
import axios, { AxiosError } from "axios"
import { useRouter } from "next/router"
import { useUserInfo } from "@today/auth/keycloak"
import { useTakerAuth } from "@today/api"
import {
  checkPassword,
  isPhoneNumberCorrect,
} from "../../../components/incheon/validations"
import { Container } from "../../../components/incheon/Container"
import { LoadingOverlay } from "../../../components/common/LoadingOverlay"
import { Label } from "../../../components/incheon/Label"

function ResetPasswordPage() {
  const fetcher = useFetcher()
  const router = useRouter()
  const taker = useTakerAuth()
  const { clientId, isAuthenticated } = useUserInfo()

  const [currentStep, setCurrentStep] = useState<
    | "inputAccountInfo"
    | "checkingAccountInfo"
    | "resetPassword"
    | "storingNewPassword"
    | "done"
  >("inputAccountInfo")

  // 사업자등록번호
  const [registrationNumber, setRegistrationNumber] = useState("")
  const formattedRegistrationNumber = useMemo(
    () =>
      [
        registrationNumber.slice(0, 3),
        registrationNumber.slice(3, 5),
        registrationNumber.slice(5),
      ]
        .filter((token) => !!token)
        .join("-"),
    [registrationNumber]
  )
  const isGoodRegistrationNumber = useMemo(
    () => /^\d{10}$/.test(registrationNumber),
    [registrationNumber]
  )

  // 담당자 휴대전화 관리
  const [phoneNumber, setPhoneNumber] = useState("")
  const formattedPhoneNumber = useMemo(
    () =>
      [
        phoneNumber.slice(0, 3),
        phoneNumber.slice(3, phoneNumber.length === 10 ? 6 : 7),
        phoneNumber.slice(phoneNumber.length === 10 ? 6 : 7),
      ]
        .filter((token) => !!token)
        .join("-"),
    [phoneNumber]
  )

  // 휴대전화 인증번호 발급
  const [
    isSendingPhoneNumberVerificationCode,
    setSendingPhoneNumberVerificationCode,
  ] = useState(false)
  const [
    hasSentPhoneNumberVerificationCode,
    setHasSentPhoneNumberVerificationCode,
  ] = useState(false)

  const verifyPhoneNumber = useCallback((phoneNumber: string) => {
    if (!isPhoneNumberCorrect(phoneNumber)) {
      return
    }
    setSendingPhoneNumberVerificationCode(true)
    ;(async () => {
      try {
        await fetcher("/api/register/send-phone-verification-code", {
          phoneNumber,
        })
      } catch {}
      setSendingPhoneNumberVerificationCode(false)
      setHasSentPhoneNumberVerificationCode(true)
    })()
  }, [])

  // 휴대전화 인증
  const [verificationCode, setVerificationCode] = useState("")
  const [verificationCodeErrorMessage, setVerificationCodeErrorMessage] =
    useState<string>()
  const [isCheckingVerificationCode, setCheckingVerificationCode] =
    useState(false)
  const [hasPhoneNumberVerified, setHasPhoneNumberVerified] = useState(false)
  const [transactionId, setTransactionId] = useState<string>()

  const checkVerificationCode = useCallback(
    (verificationCode: string) => {
      setCheckingVerificationCode(true)
      setVerificationCodeErrorMessage(undefined)
      ;(async () => {
        try {
          const data = await fetcher(
            "/api/register/check-phone-verification-code",
            {
              phoneNumber,
              verificationCode,
            }
          )
          if (data.transactionId) {
            setTransactionId(data.transactionId)
          }
          setHasPhoneNumberVerified(true)
          setCheckingVerificationCode(false)
          setVerificationCode("")
        } catch (error) {
          const defaultErrorMessage =
            "에러가 발생하였습니다. 잠시 후 다시 시도해주세요."
          const errorMessage = axios.isAxiosError(error)
            ? (error as AxiosError).response?.data?.errorMessage ??
              defaultErrorMessage
            : defaultErrorMessage
          setVerificationCodeErrorMessage(errorMessage)
          setCheckingVerificationCode(false)
        }
      })()
    },
    [phoneNumber]
  )

  useEffect(() => {
    setVerificationCodeErrorMessage(undefined)
    if (verificationCode.length === 6) {
      setHasPhoneNumberVerified(false)
      checkVerificationCode(verificationCode)
    }
  }, [checkVerificationCode, verificationCode])

  // 회원 정보 존재 여부 조회
  const [client, setClient] = useState<Client>()

  useEffect(() => {
    ;(async () => {
      if (!clientId) return
      const client = await taker.retrieveClient()
      setClient(client)
    })()
  }, [clientId])

  const [invalidAccount, setInvalidAccount] = useState<
    "authenticated" | "anonymous"
  >()
  useEffect(() => {
    setTransactionId(undefined)
    setInvalidAccount(undefined)
  }, [registrationNumber, phoneNumber])

  const checkAccountInfo = useCallback(async () => {
    if (!registrationNumber || !phoneNumber || !transactionId) {
      return
    }
    if (isAuthenticated && client) {
      if (
        client.registrationNumber.replaceAll("-", "") !==
          registrationNumber.replaceAll("-", "") ||
        client.phone.replaceAll("-", "") !== phoneNumber.replaceAll("-", "")
      ) {
        setInvalidAccount("authenticated")
        setHasPhoneNumberVerified(false)
        setHasSentPhoneNumberVerificationCode(false)
        setTransactionId(undefined)
        setCurrentStep("inputAccountInfo")
        return
      }
    }
    setInvalidAccount(undefined)
    setCurrentStep("checkingAccountInfo")
    try {
      const client = await fetcher<Client>("/api/register/check-account", {
        registrationNumber,
        phoneNumber,
        transactionId,
      })
      setClient(client)
      setCurrentStep("resetPassword")
    } catch {
      setInvalidAccount("anonymous")
      setHasPhoneNumberVerified(false)
      setHasSentPhoneNumberVerificationCode(false)
      setTransactionId(undefined)
      setCurrentStep("inputAccountInfo")
    }
  }, [isAuthenticated, client, registrationNumber, phoneNumber, transactionId])

  // 비밀번호 관리
  const [password, setPassword] = useState("")
  const { isGoodPassword, passwordErrorMessage } = useMemo(
    () => checkPassword(password),
    [password]
  )
  const [hasPasswordChecked, setHasPasswordChecked] = useState(false)

  // 비밀번호 확인
  const [repeatedPassword, setRepeatedPassword] = useState("")
  const [hasRepeatedPasswordChecked, setHasRepeatedPasswordChecked] =
    useState(false)
  const [showPasswordsMismatched, setShowPasswordsMismatched] = useState(false)
  useEffect(() => {
    setShowPasswordsMismatched(false)
  }, [repeatedPassword, password])

  // 비밀번호 변경
  const canChangePassword =
    transactionId && isGoodPassword && password && password === repeatedPassword

  const changePassword = useCallback(async () => {
    if (!canChangePassword) {
      return
    }
    setCurrentStep("storingNewPassword")
    try {
      await fetcher("/api/register/change-password", {
        transactionId,
        password,
      })
      setCurrentStep("done")
    } catch {
      setCurrentStep("resetPassword")
      alert("에러가 발생했습니다. 잠시 후 다시 시도해주세요.")
    }
  }, [canChangePassword, transactionId, password])

  return (
    <Container>
      {currentStep === "checkingAccountInfo" ||
      currentStep === "storingNewPassword" ? (
        <div
          className="fixed left-0 top-0 h-screen w-screen"
          style={{ height: "100dvh" }}
        >
          <LoadingOverlay />
        </div>
      ) : currentStep === "done" ? (
        <>
          <div className="flex flex-1 flex-col items-center justify-center text-2xl">
            <p className="text-3xl">
              <span className="font-bold">{client?.companyName}</span>님,
            </p>
            <p className="mt-2">비밀번호가 정상적으로 변경되었습니다.</p>
            <Button className="my-12" onClick={() => router.replace("/orders")}>
              로그인 페이지로 이동하기
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="mb-8 flex flex-col gap-y-1 text-2xl font-bold md:mb-12 md:text-3xl">
            {client?.companyName ? (
              <>
                <p>
                  {client.companyName}
                  <span className="font-normal">님,</span>
                </p>
                <p>새로운 비밀번호를 입력해주세요.</p>
              </>
            ) : (
              <p>비밀번호 재설정</p>
            )}
          </div>
          <div className="relative h-full w-full flex-1 overflow-y-scroll">
            <div className="flex h-full flex-col justify-start">
              <div>
                <FormControl
                  label={
                    <Label bold tip="(10자리 숫자)">
                      사업자등록번호
                    </Label>
                  }
                >
                  <Input
                    inputMode="numeric"
                    disabled={currentStep !== "inputAccountInfo"}
                    value={formattedRegistrationNumber}
                    onChange={(event) =>
                      setRegistrationNumber(
                        event.target.value.replace(/\D/g, "").slice(0, 10)
                      )
                    }
                    endEnhancer={
                      !invalidAccount &&
                      isGoodRegistrationNumber &&
                      (!isAuthenticated ||
                        client?.registrationNumber ===
                          formattedRegistrationNumber) ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : invalidAccount ||
                        (isAuthenticated &&
                          isGoodRegistrationNumber &&
                          client?.registrationNumber !==
                            formattedRegistrationNumber) ? (
                        <FaTimesCircle className="text-red-500" />
                      ) : null
                    }
                  />
                </FormControl>
              </div>
              <div>
                <FormControl label={<Label bold>담당자 휴대전화</Label>}>
                  <Input
                    value={formattedPhoneNumber}
                    autoComplete={
                      currentStep === "inputAccountInfo"
                        ? "off"
                        : "mobile tel-national"
                    }
                    type="tel"
                    inputMode="tel"
                    disabled={currentStep !== "inputAccountInfo"}
                    onChange={(event) => {
                      const phoneNumber = event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 11)
                      setHasSentPhoneNumberVerificationCode(false)
                      setHasPhoneNumberVerified(false)
                      setPhoneNumber(phoneNumber)
                    }}
                    endEnhancer={
                      transactionId ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : currentStep === "inputAccountInfo" &&
                        !isPhoneNumberCorrect(phoneNumber) ? (
                        phoneNumber.length >= 10 && (
                          <FaExclamationTriangle className="text-yellow-500" />
                        )
                      ) : currentStep === "inputAccountInfo" &&
                        isPhoneNumberCorrect(phoneNumber) &&
                        isAuthenticated &&
                        client?.phone.replaceAll("-", "") !==
                          phoneNumber.replaceAll("-", "") ? (
                        <FaTimesCircle className="text-red-500" />
                      ) : currentStep === "inputAccountInfo" &&
                        isPhoneNumberCorrect(phoneNumber) ? (
                        <Button
                          size="mini"
                          isLoading={isSendingPhoneNumberVerificationCode}
                          disabled={isSendingPhoneNumberVerificationCode}
                          onClick={() => verifyPhoneNumber(phoneNumber)}
                        >
                          {hasSentPhoneNumberVerificationCode
                            ? "재전송"
                            : "인증"}
                        </Button>
                      ) : null
                    }
                  />
                </FormControl>
              </div>
              {hasSentPhoneNumberVerificationCode &&
                !hasPhoneNumberVerified && (
                  <div>
                    <FormControl
                      label={
                        <Label
                          bold
                          tip={
                            <span className="text-red-500">
                              (카카오톡 또는 SMS를 확인해주세요)
                            </span>
                          }
                        >
                          6자리 인증코드 입력
                        </Label>
                      }
                      caption={
                        verificationCodeErrorMessage && (
                          <p className="text-red-500">
                            * {verificationCodeErrorMessage}
                          </p>
                        )
                      }
                    >
                      <Input
                        value={verificationCode}
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        onChange={(event) => {
                          const verificationCode = event.target.value
                            .replace(/\D/g, "")
                            .slice(0, 6)
                          setVerificationCode(verificationCode)
                        }}
                        endEnhancer={
                          isCheckingVerificationCode ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            verificationCodeErrorMessage &&
                            verificationCode.length === 6 && (
                              <FaTimesCircle className="text-red-500" />
                            )
                          )
                        }
                      />
                    </FormControl>
                  </div>
                )}
              {invalidAccount === "anonymous" ? (
                <p className="mt-4 text-red-500">
                  가입되지 않은 사업자등록번호이거나, 담당자 휴대전화가 일치하지
                  않습니다.
                </p>
              ) : invalidAccount === "authenticated" ? (
                <p className="mt-4 text-red-500">
                  사업자등록번호 또는 담당자 휴대전화가 가입된 내역과 일치하지
                  않습니다.
                </p>
              ) : null}
              {currentStep === "resetPassword" && (
                <>
                  <div className="animate-fadeIn">
                    <FormControl
                      label={<Label bold>새 비밀번호</Label>}
                      caption={
                        hasPasswordChecked &&
                        password.length > 0 &&
                        !isGoodPassword && (
                          <p className="text-red-500">
                            * {passwordErrorMessage}
                          </p>
                        )
                      }
                    >
                      <Input
                        value={password}
                        type="password"
                        autoComplete="new-password"
                        onChange={(event) => {
                          setPassword(event.target.value)
                        }}
                        onBlur={() => {
                          if (password.length > 0) {
                            setHasPasswordChecked(true)
                          }
                        }}
                        endEnhancer={
                          isGoodPassword ? (
                            <FaCheckCircle className="text-green-500" />
                          ) : (
                            password.length > 0 && (
                              <FaExclamationTriangle className="text-yellow-500" />
                            )
                          )
                        }
                        overrides={{
                          Input: {
                            props: {
                              minLength: "10",
                              maxLength: "32",
                              passwordrules:
                                "required: digit; required: upper,lower; allowed: [!#$%&'()*+,./:;<=>?@\\^_`{|}~];",
                            },
                          },
                        }}
                      />
                    </FormControl>
                  </div>
                  <div className="animate-fadeIn">
                    <FormControl
                      label={
                        <Label
                          bold
                          tip="(입력하신 비밀번호를 한 번 더 입력해주세요)"
                        >
                          새 비밀번호 확인
                        </Label>
                      }
                      caption={
                        showPasswordsMismatched && (
                          <p className="text-red-500">
                            * 비밀번호가 일치하지 않습니다.
                          </p>
                        )
                      }
                    >
                      <Input
                        value={repeatedPassword}
                        type="password"
                        autoComplete="new-password"
                        onChange={(event) => {
                          setRepeatedPassword(event.target.value)
                        }}
                        onBlur={() => {
                          if (repeatedPassword.length === 0) {
                            return
                          }
                          setHasRepeatedPasswordChecked(true)
                          if (password !== repeatedPassword) {
                            setShowPasswordsMismatched(true)
                          }
                        }}
                        endEnhancer={
                          password.length === 0 ? null : repeatedPassword ===
                            password ? (
                            <FaCheckCircle className="text-green-500" />
                          ) : (
                            hasRepeatedPasswordChecked && (
                              <FaTimesCircle className="text-red-500" />
                            )
                          )
                        }
                      />
                    </FormControl>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex w-full">
            <Button
              className="flex-grow"
              disabled={
                (currentStep === "inputAccountInfo" &&
                  (!isGoodRegistrationNumber || !hasPhoneNumberVerified)) ||
                (currentStep === "resetPassword" && !canChangePassword)
              }
              onClick={() => {
                switch (currentStep) {
                  case "inputAccountInfo":
                    if (isGoodRegistrationNumber && hasPhoneNumberVerified) {
                      checkAccountInfo()
                    }
                    return
                  case "resetPassword":
                    if (canChangePassword) {
                      changePassword()
                    }
                }
              }}
            >
              비밀번호 변경
            </Button>
          </div>
        </>
      )}
    </Container>
  )
}

export default ResetPasswordPage
